import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useGlobalFilters } from "../lib/globalFilters";
import {
  PREFETCHABLE_ROUTES,
  isPrefetchableRoute,
  routeWarmTargets,
} from "./routeWarming";
import { useApiClient } from "./useApiClient";

/**
 * Returns a stable-ish callback that warms the React Query cache for a primary
 * nav destination, so the page renders from cache instead of waiting on a fetch
 * after the click. Driven both on link hover/focus and during post-load idle
 * time (see NavPrefetcher). The warmed params mirror each page's default,
 * no-screen query keyed off the current filters; if they don't line up (e.g. an
 * active saved screen) the page simply fetches normally.
 */
export function useNavPrefetch() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const filters = useGlobalFilters();

  return useCallback(
    (route: string) => {
      if (!isPrefetchableRoute(route)) {
        return;
      }
      // prefetchQuery is a no-op when the entry is already fresh (within the
      // default staleTime), so hovering after an idle warm won't refetch.
      for (const target of routeWarmTargets(client, route, filters)) {
        void queryClient.prefetchQuery(target);
      }
    },
    [client, queryClient, filters],
  );
}

export { PREFETCHABLE_ROUTES, isPrefetchableRoute };
